import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';

extendZodWithOpenApi(z);

/**
 * Run presets seeded by the fan-out framework's startLazyRun. Must stay in sync with the
 * framework's `RunPreset` — `'poll'` (targeted poll-preset runs, e.g. clickup-task-reconcile)
 * is a real value the DB stores, so it MUST be listed here or `queryConnectionSyncRuns`
 * throws a ZodError and the sync-runs endpoint 500s for any connection that has polled.
 */
export const SyncRunPresetSchema = z.enum([
  'initialize',
  'synchronize',
  'operations',
  'webhook',
  'poll',
]);
export type SyncRunPreset = z.infer<typeof SyncRunPresetSchema>;

/**
 * sync_run lifecycle: 'running' until `outstanding` drains to 0, then one of the
 * terminal statuses ('failed' is stamped by the sweeper on wedged runs).
 */
export const SyncRunStatusSchema = z.enum([
  'running',
  'completed',
  'completed_with_errors',
  'failed',
]);
export type SyncRunStatus = z.infer<typeof SyncRunStatusSchema>;

/**
 * Public projection of a sync_run row. NEVER includes `context` — it captures the
 * caller's PostOperationCallbackContext verbatim and may contain PII.
 *
 * Progress: total = outstanding + ok_count + error_count (grows while the fan-out
 * discovers units), completed = ok_count + error_count (only ever rises).
 */
export const SyncRunProgressSchema = z.object({
  id: z.string().uuid(),
  connection_id: z.string().uuid(),
  preset: SyncRunPresetSchema,
  status: SyncRunStatusSchema,
  outstanding: z.number().int(),
  ok_count: z.number().int(),
  error_count: z.number().int(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  finalized_at: z.string().nullable(),
  updated_at: z.string(),
});
export type SyncRunProgress = z.infer<typeof SyncRunProgressSchema>;
